<template>
  <div id="exchange-page">
    <!-- ============ Hero (navy gradient card, Wallet-style) ============ -->
    <section class="hero-section">
      <div class="hero-card">
        <div class="hero-head">
          <div class="hero-text">
            <h1 class="hero-title">GetEquity Exchange</h1>
            <p class="hero-subtitle">
              Buy and sell investments from other investors in the secondary market.
            </p>
            <div class="market-status">
              <span class="dot"></span>
              Market open • {{ currencyListings.length }} live
              {{ currencyListings.length === 1 ? "listing" : "listings" }}
            </div>
          </div>

          <!-- Currency dropdown (only when >1 currency) -->
          <div v-if="currencies.length > 1" class="currency-select">
            <button class="currency-trigger" type="button" @click="currencyOpen = !currencyOpen">
              <ExIcon name="payments" class="lead" />
              {{ activeCurrency }}
              <ExIcon name="chevron" class="chevron" />
            </button>
            <template v-if="currencyOpen">
              <div class="currency-backdrop" @click="currencyOpen = false"></div>
              <ul class="currency-menu">
                <li
                  v-for="c in currencies"
                  :key="c"
                  :class="{ active: c === activeCurrency }"
                  @click="selectCurrency(c)"
                >
                  <span class="cur-code">{{ c }}</span>
                  <span class="cur-name">{{ currencyName(c) }}</span>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ Market Overview (Wallet-style stat cards) ============ -->
    <section class="overview-stats">
      <StatCard icon="bar-chart" variant="blue" label="Volume This Week" :value="volumeLabel" />
      <StatCard icon="storefront" variant="purple" label="Available Listings" :value="listingsCount" />
      <StatCard icon="percent" variant="green" label="Average Yield" :value="avgYieldLabel" />
      <StatCard icon="swap" variant="orange" label="Trades This Week" :value="tradesWeek" />
    </section>

    <!-- ============ Filters + Search ============ -->
    <div class="filters-row">
      <button
        v-for="chip in chips"
        :key="chip"
        class="chip"
        :class="{ active: selectedFilter === chip }"
        type="button"
        @click="selectedFilter = chip"
      >
        {{ chip }}
      </button>
    </div>

    <div class="search-bar">
      <ExIcon name="search" class="search-icon" />
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search by company, symbol or type…"
        class="search-input"
      />
      <button v-if="searchQuery" class="search-clear" type="button" @click="searchQuery = ''">
        <ExIcon name="close" />
      </button>
    </div>

    <!-- ============ Body ============ -->
    <!-- Loading shimmer -->
    <div v-if="loading" class="shimmer-grid">
      <div v-for="n in 6" :key="n" class="shimmer-card"></div>
    </div>

    <!-- No listings at all -->
    <div v-else-if="!currencyListings.length" class="empty-state">
      <ExIcon name="box" class="empty-icon" />
      <p class="empty-title">No live listings yet</p>
      <p class="empty-sub">Check back soon. New secondaries are listed regularly.</p>
    </div>

    <!-- Filtered Results -->
    <div v-else-if="isFilteredMode" class="filtered">
      <p class="filtered-count">
        {{ filteredResults.length }}
        {{ filteredResults.length === 1 ? "investment" : "investments" }}
      </p>
      <div v-if="filteredResults.length" class="tile-list">
        <MarketTile v-for="t in filteredResults" :key="t._id || t.id" :token="t" />
      </div>
      <div v-else class="empty-state small">
        <ExIcon name="search-off" class="empty-icon" />
        <p class="empty-title">No investments found</p>
        <p class="empty-sub">Try a different search or filter</p>
      </div>
    </div>

    <!-- Discovery Dashboard -->
    <div v-else class="discovery">
      <!-- Top Opportunities -->
      <div class="d-section" v-if="topOpportunities.length">
        <SectionHeader
          title="Top Opportunities"
          subtitle="Best picks across yield, availability and momentum"
          icon="fire"
          color="#F2994A"
        />
        <div class="card-grid">
          <OpportunityCard v-for="t in topOpportunities" :key="t._id || t.id" :token="t" />
        </div>
      </div>

      <!-- Commodities: metals, energy and crops, priced from the market every weekday. -->
      <div class="d-section" v-if="commodities.length">
        <SectionHeader
          title="Commodities"
          :subtitle="commoditiesSubtitle"
          icon="box"
          color="#8A6A1C"
          :see-all="commodities.length > 4"
          @see-all="selectedFilter = 'Commodity'"
        />
        <div class="card-grid">
          <OpportunityCard v-for="t in commodities.slice(0, 4)" :key="t._id || t.id" :token="t" />
        </div>
      </div>

      <!-- NGX stocks: a real share behind every token, priced at the NGX close. -->
      <div class="d-section" v-if="ngxListed.length">
        <SectionHeader
          title="Listed on NGX"
          subtitle="Shares on the Nigerian Exchange, priced at each day's close"
          icon="bar-chart"
          color="#4F46E5"
          :see-all="ngxListed.length > 4"
          @see-all="selectedFilter = 'NGX Stock'"
        />
        <div class="card-grid">
          <OpportunityCard v-for="t in ngxListed.slice(0, 4)" :key="t._id || t.id" :token="t" />
        </div>
      </div>

      <!-- Recently Listed -->
      <div class="d-section" v-if="recentlyListed.length">
        <SectionHeader
          title="Recently Listed"
          subtitle="Fresh secondaries just added to the market"
          icon="clock"
          color="#4D84FF"
          :see-all="true"
          @see-all="selectedFilter = 'Recently Listed'"
        />
        <div class="card-grid">
          <OpportunityCard
            v-for="t in recentlyListed"
            :key="t._id || t.id"
            :token="t"
            :tag="listedTag(t)"
          />
        </div>
      </div>

      <!-- Highest Yield -->
      <div class="d-section" v-if="highestYield.length">
        <SectionHeader
          title="Highest Yield"
          subtitle="Top fixed-income returns on the exchange"
          icon="trending-up"
          color="#F2994A"
          :see-all="true"
          @see-all="selectedFilter = 'Highest Yield'"
        />
        <div class="rank-list">
          <RankTile
            v-for="(t, i) in highestYield"
            :key="t._id || t.id"
            :token="t"
            :rank="i + 1"
          />
        </div>
      </div>

      <!-- Maturing Soon -->
      <div class="d-section" v-if="maturingSoon.length">
        <SectionHeader
          title="Maturing Soon"
          subtitle="Fixed-income nearing maturity"
          icon="hourglass"
          color="#E84343"
          :see-all="true"
          @see-all="selectedFilter = 'Maturing Soon'"
        />
        <div class="card-grid">
          <OpportunityCard
            v-for="t in maturingSoon"
            :key="t._id || t.id"
            :token="t"
            :tag="maturingTag(t)"
          />
        </div>
      </div>

      <!-- Available Tokens: what can be bought right now, as the mobile app names it -->
      <div class="d-section" v-if="upForSale.length">
        <SectionHeader
          title="Available Tokens"
          subtitle="Investment tokens available to buy now"
          icon="tag"
          color="#4E84FF"
        />
        <div class="card-grid">
          <OpportunityCard
            v-for="t in upForSale"
            :key="t._id || t.id"
            :token="t"
            :hide-details="true"
            :tag="availableTag(t)"
          />
        </div>
      </div>

      <!-- Market Activity -->
      <div class="d-section">
        <SectionHeader
          title="Market Activity"
          subtitle="Recent trades across the exchange"
          icon="bolt"
          color="#4E84FF"
        />
        <div class="activity-card">
          <div v-if="marketActivityLoading" class="activity-loading">
            <span class="spinner"></span>
            <span>Loading recent trades…</span>
          </div>
          <div v-else-if="!recentActivity.length" class="activity-empty">
            <ExIcon name="bolt" class="empty-icon" />
            <p>No recent trades yet</p>
          </div>
          <div v-else class="activity-list">
            <ActivityTile
              v-for="(item, i) in recentActivity"
              :key="i"
              :activity="item"
              :route="activityRoute(item)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ExIcon from "@/components/exchange/ExIcon.vue";
import StatCard from "@/components/exchange/StatCard.vue";
import SectionHeader from "@/components/exchange/SectionHeader.vue";
import OpportunityCard from "@/components/exchange/OpportunityCard.vue";
import MarketTile from "@/components/exchange/MarketTile.vue";
import RankTile from "@/components/exchange/RankTile.vue";
import ActivityTile from "@/components/exchange/ActivityTile.vue";
import { setItem, getItem } from "@/utils/storage.js";
import * as ex from "@/utils/exchange.js";
import { DealKinds, listingView } from "@/utils/trade-view";

/** Deal-type chips: label as the badges name it, and the badge tone it filters on. */
const TYPE_CHIPS = Object.freeze({
  Debt: "debt",
  Equity: "equity",
  SAFE: "safe",
  Fund: "fund",
  Commodity: "commodity",
  "NGX Stock": "ngx",
});

/** Every deal the sponsor marked Sharia compliant, whatever its type. */
const SHARIA_CHIP = "Sharia Compliant";

const CURRENCY_KEY = "exchange_currency";

export default {
  name: "Exchange",

  components: {
    ExIcon,
    StatCard,
    SectionHeader,
    OpportunityCard,
    MarketTile,
    RankTile,
    ActivityTile,
  },

  data() {
    return {
      activeCurrency: "USD",
      selectedFilter: "All",
      searchQuery: "",
      currencyOpen: false,
    };
  },

  computed: {
    ...mapState({
      listings: (state) => state.TokenModule.exchangeListings,
      raising: (state) => state.TokenModule.exchangeRaising,
      loaded: (state) => state.TokenModule.exchangeListingsLoaded,
      pageLoading: (state) => state.loading,
      marketActivity: (state) => state.TokenModule.marketActivity,
      exchangeStats: (state) => state.TokenModule.exchangeStats,
      marketActivityLoading: (state) => state.TokenModule.marketActivityLoading,
    }),

    loading() {
      return this.pageLoading && !this.loaded;
    },

    currencies() {
      return ex.distinctCurrencies(this.listings);
    },

    currencyListings() {
      return (this.listings || []).filter(
        (t) => ex.tokenCurrency(t).toUpperCase() === this.activeCurrency
      );
    },

    // "Now raising" companies, scoped to the active currency — the source for
    // the Recently Listed section (feed: /tokens/raising).
    raisingListings() {
      return (this.raising || []).filter(
        (t) => ex.tokenCurrency(t).toUpperCase() === this.activeCurrency
      );
    },

    // ---- Market overview metrics ----
    // Counted on the server (/transaction/market-stats). They were worked out here from what the
    // page had loaded: trades from a feed fetched 20 rows at a time, so the count stopped at 20
    // and saw each trade twice (its buy row and its sell row); and "volume" was the listings'
    // market value, not money traded. Until the figures arrive, listings and yield fall back to
    // the listings already loaded, which mean the same; the week's trades and volume have no
    // honest fallback, so they show a dash.
    stats() {
      const s = this.exchangeStats;
      return s && s.currency === this.activeCurrency ? s : null;
    },
    volumeLabel() {
      return this.stats ? ex.compactMoney(this.stats.volumeThisWeek, this.activeCurrency) : "—";
    },
    listingsCount() {
      return this.stats ? this.stats.availableListings : this.currencyListings.length;
    },
    avgYieldLabel() {
      const value = this.stats ? this.stats.averageYield : ex.averageYield(this.currencyListings);
      return `${Number(value).toFixed(1)}%`;
    },
    tradesWeek() {
      return this.stats ? this.stats.tradesThisWeek : "—";
    },

    // ---- Filter chips ----
    // Every deal type, named as the badges name them, whether or not one is listed today, so
    // investors can see metals and NGX stocks are on offer here.
    chips() {
      return ["All", ...Object.keys(TYPE_CHIPS), SHARIA_CHIP, "Highest Yield", "Recently Listed", "Maturing Soon"];
    },

    isFilteredMode() {
      return this.searchQuery.trim().length > 0 || this.selectedFilter !== "All";
    },

    // Everything on offer in the active currency: the exchange listings and the raises still open.
    // Equity sits in Now Raising until its raise completes and a fund never completes one, so the
    // exchange listings alone have none of either. Metals and NGX stocks are in both feeds.
    offeredListings() {
      const seen = new Set();
      return [...this.currencyListings, ...this.raisingListings].filter((t) => {
        const id = t._id || t.id;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },

    // ---- Filtered Results (§7) ----
    filteredResults() {
      // Recently Listed draws from the raising feed, Highest Yield and Maturing Soon from the
      // secondary-market (raised) listings; search and the deal-type chips cover both.
      if (this.selectedFilter === "Recently Listed") {
        return ex.sortByListedDesc(
          ex.searchListings(this.raisingListings, this.searchQuery)
        );
      }
      switch (this.selectedFilter) {
        case "All":
          return ex.searchListings(this.offeredListings, this.searchQuery);
        case "Highest Yield":
          return ex.sortByYieldDesc(ex.searchListings(this.currencyListings, this.searchQuery));
        case "Maturing Soon":
          return ex.maturingSoon(ex.searchListings(this.currencyListings, this.searchQuery));
        case SHARIA_CHIP:
          return ex.searchListings(this.offeredListings, this.searchQuery).filter((t) => t.sharia_compliant === true);
        default:
          // deal-type chip
          return ex.searchListings(this.offeredListings, this.searchQuery)
            .filter((t) => listingView(t).badge.tone === TYPE_CHIPS[this.selectedFilter]);
      }
    },

    // ---- Discovery Dashboard (§8) ----
    topOpportunities() {
      return [...this.currencyListings]
        .sort((a, b) => ex.opportunityScore(b) - ex.opportunityScore(a))
        .slice(0, 8);
    },
    commodities() {
      return this.currencyListings.filter((t) => listingView(t).kind === DealKinds.COMMODITY);
    },
    // "Vaulted metal" only while every commodity listed is a metal; oil and wheat are not vaulted.
    commoditiesSubtitle() {
      const allMetal = this.commodities.every((t) => (t.market?.commodity_kind || "metal") === "metal");
      return allMetal
        ? "Vaulted metal, priced from the spot in your currency every weekday"
        : "Metals, energy and crops, priced from the market in your currency every weekday";
    },
    ngxListed() {
      return this.currencyListings.filter((t) => listingView(t).kind === DealKinds.LISTED);
    },
    recentlyListed() {
      return ex.sortByListedDesc(this.raisingListings).slice(0, 4);
    },
    highestYield() {
      return ex
        .sortByYieldDesc(this.currencyListings.filter((t) => ex.isFixedIncome(t)))
        .slice(0, 5);
    },
    maturingSoon() {
      return ex
        .maturingSoon(this.currencyListings)
        .filter((t) => ex.isFixedIncome(t))
        .slice(0, 8);
    },
    upForSale() {
      return this.currencyListings
        .filter((t) => ex.sellOrderTotal(t) > 0)
        // Most on offer first, as on mobile.
        .sort((a, b) => ex.sellOrderTotal(b) - ex.sellOrderTotal(a))
        .slice(0, 5);
    },
    recentActivity() {
      return (this.marketActivity || []).slice(0, 6);
    },

    // Lookup map for resolving activity items to a known listing, across both
    // the secondary (raised) and primary (raising) feeds — used to pull an
    // accurate symbol for the token-detail link.
    listingIndex() {
      const map = {};
      [...(this.listings || []), ...(this.raising || [])].forEach((t) => {
        if (t._id) map[t._id] = t;
        if (t.id) map[t.id] = t;
        if (t.assetId) map[t.assetId] = t;
      });
      return map;
    },
  },

  watch: {
    // Selecting a currency resets the filter and refetches market activity.
    activeCurrency(val) {
      this.selectedFilter = "All";
      this.searchQuery = "";
      setItem(CURRENCY_KEY, val);
      this.getMarketActivity({ currency: val });
      this.getExchangeStats({ currency: val });
    },
  },

  async created() {
    await this.getExchangeListings({ page: 1, limit: 100 });
    this.activeCurrency = ex.resolveActiveCurrency(this.listings, getItem(CURRENCY_KEY));
    this.getExchangeRaising({ page: 1, limit: 100 });
    this.getMarketActivity({ currency: this.activeCurrency });
    this.getExchangeStats({ currency: this.activeCurrency });
  },

  methods: {
    ...mapActions("TokenModule", [
      "getExchangeListings",
      "getExchangeRaising",
      "getMarketActivity",
      "getExchangeStats",
    ]),

    currencyName: ex.currencyName,

    selectCurrency(c) {
      this.currencyOpen = false;
      this.activeCurrency = c;
    },

    listedTag(token) {
      const d = ex.daysSinceListed(token);
      const label =
        d <= 0 ? "Listed today" : `Listed ${ex.relativeTime(ex.listedDate(token))}`;
      return { label, color: "#4D84FF" };
    },

    maturingTag(token) {
      const d = ex.daysRemaining(token);
      return { label: `Matures in ${d} ${d === 1 ? "day" : "days"}`, color: "#E84343" };
    },
    availableTag(token) {
      return {
        label: `${ex.compact(ex.sellOrderTotal(token))} available`,
        color: "#4E84FF",
      };
    },

    activityRoute(item) {
      const token = item.token || {};
      const id = token._id || token.id || token.assetId;
      if (!id) return null;
      const match =
        this.listingIndex[token._id] ||
        this.listingIndex[token.id] ||
        this.listingIndex[token.assetId];
      // Always route by token id; use the matched listing's symbol when known,
      // else fall back to the token's own symbol/currency for the URL slug.
      const symbol = (match && match.symbol) || token.symbol || token.currency || "token";
      return {
        name: "TokenOverview",
        params: { symbol, id: (match && (match._id || match.id)) || id },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
#exchange-page {
  padding-bottom: 40px;
}

/* ============ Hero ============ */
.hero-section {
  margin-bottom: 24px;
}

.hero-card {
  background: linear-gradient(135deg, #001240 0%, #0a2463 50%, #001240 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  padding: 28px;
  position: relative;
  // Above the sections below it, so the currency menu overlaps them rather than sliding under.
  z-index: 2;

  // No `overflow: hidden` here: it clipped the currency menu at the card edge, which no
  // z-index on the menu can escape. It was only ever holding the wash below inside the
  // rounded corners, and that is the wash's own job.
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.02) 0%, transparent 50%);
    pointer-events: none;
  }
}

.hero-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.hero-title {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  color: #ffffff;
}

.hero-subtitle {
  margin: 6px 0 0;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  max-width: 480px;
}

.market-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #27ae60;
    box-shadow: 0 0 0 3px rgba(39, 174, 96, 0.25);
  }
}

/* Currency dropdown */
.currency-select {
  position: relative;
  flex-shrink: 0;
}

.currency-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.25);
  }

  .lead {
    font-size: 18px;
  }
  .chevron {
    font-size: 18px;
    opacity: 0.7;
  }
}

.currency-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20;
}

.currency-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 21;
  min-width: 200px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  box-shadow: 0 12px 32px rgba(0, 18, 64, 0.18);

  li {
    display: flex;
    flex-direction: column;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #f8fafc;
    }
    &.active {
      background: #eef2ff;
    }
  }

  .cur-code {
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
  }
  .cur-name {
    font-size: 11px;
    color: #64748b;
  }
}

/* ============ Market overview ============ */
.overview-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 28px;

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* ============ Filters ============ */
.filters-row {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 16px;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.chip {
  flex-shrink: 0;
  padding: 9px 16px;
  border-radius: 0;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    color: #0f172a;
    border-color: #cbd5e1;
  }
  &.active {
    background: #001240;
    border-color: #001240;
    color: #ffffff;
  }
}

/* ============ Search ============ */
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 46px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  margin-bottom: 28px;

  .search-icon {
    font-size: 20px;
    color: #8791a7;
  }
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #0f172a;

  &::placeholder {
    color: #8791a7;
  }
}

.search-clear {
  display: flex;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #8791a7;
  font-size: 18px;

  &:hover {
    color: #0f172a;
  }
}

/* ============ Discovery ============ */
.discovery {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.rank-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Filtered results */
.filtered-count {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}

.tile-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Activity card */
.activity-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 8px 20px;
}

.activity-list {
  display: flex;
  flex-direction: column;

  > * + * {
    border-top: 1px solid #eef1f6;
  }
}

.activity-loading,
.activity-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: #8791a7;
  font-size: 13px;

  .empty-icon {
    font-size: 28px;
  }
  p {
    margin: 0;
  }
}

.activity-loading {
  flex-direction: row;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #d0d5e0;
  border-top-color: #4e84ff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 64px 16px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 7px;

  .empty-icon {
    font-size: 40px;
    color: #cbd5e1;
  }
  .empty-title {
    margin: 8px 0 0;
    font-size: 15px;
    font-weight: 500;
    color: #0f172a;
  }
  .empty-sub {
    margin: 0;
    font-size: 13px;
    color: #64748b;
  }

  &.small {
    padding: 40px 16px;
  }
}

/* Shimmer */
.shimmer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.shimmer-card {
  height: 240px;
  border-radius: 7px;
  background: linear-gradient(100deg, #eef1f6 30%, #f7f9fd 50%, #eef1f6 70%);
  background-size: 200% 100%;
  animation: shimmer 1.3s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 640px) {
  .hero-card {
    padding: 20px;
  }
  .hero-head {
    flex-direction: column;
  }
  .currency-select {
    align-self: flex-start;
  }
}
</style>
